import {
  AlertCircle,
  Backpack,
  Bike,
  CheckCircle,
  Hand,
  Heart,
  Lock,
  MapPin,
  Minus,
  Package,
  Plus,
  Shield,
  Zap
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { API_ENDPOINTS, buildApiUrl } from '../config/api';
import { useBooking } from '../contexts/BookingContext';
import { useLanguage } from '../contexts/LanguageContext';
import { calculateDurationInDays } from '../services/pricingService';
import '../styles/accessory-selection-light.css';
import '../styles/price-breakdown-light.css';
import { getTranslation } from '../translations';
import { formatPricePerDay, formatPriceWithEuro } from '../utils/priceFormatter';
import { notifySupportApiNetworkError, notifySupportApiServerError } from '../utils/supportApiErrorNotifier';
import PriceBreakdown from './PriceBreakdown';
import StepContainer from './StepContainer';

const AccessorySelection = ({ selectedVehicles, selectedDates, selectedAccessories: initialSelectedAccessories, onNext, onBack, onPricingUpdate }) => {
  const [selectedAccessories, setSelectedAccessories] = useState(initialSelectedAccessories || {});
  const [accessoriesByModel, setAccessoriesByModel] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [errorStates, setErrorStates] = useState({});
  const { language } = useLanguage();
  const { bookingData, updateBookingData } = useBooking();

  // No sincronizar estado local con la prop en cada cambio: la prop viene del contexto y,
  // al actualizar el contexto desde handleAccessoryToggle, a veces el padre re-renderiza
  // con el valor anterior antes de que el contexto se actualice, lo que sobrescribía la
  // selección y producía flickering. El valor inicial solo se usa al montar; al volver
  // al paso el componente se monta de nuevo con el valor actual del contexto.

  // Cargar accesorios para cada modelo de vehículo
  useEffect(() => {
    if (!selectedVehicles || selectedVehicles.length === 0) {
      return;
    }

    const loadAccessoriesForModels = async () => {
      const newAccessoriesByModel = {};
      const newLoadingStates = {};
      const newErrorStates = {};

      // Inicializar estados
      selectedVehicles.forEach(vehicle => {
        if (vehicle.modelId) {
          newLoadingStates[vehicle.modelId] = true;
          newErrorStates[vehicle.modelId] = null;
          newAccessoriesByModel[vehicle.modelId] = [];
        }
      });

      setLoadingStates(newLoadingStates);
      setErrorStates(newErrorStates);

      // Cargar accesorios para cada modelo
      for (const vehicle of selectedVehicles) {
        if (!vehicle.modelId) {
          continue;
        }

        try {
          // Incluir el idioma en la petición
          const url = `${buildApiUrl(`${API_ENDPOINTS.ACCESSORIES}/model/${vehicle.modelId}`)}?lang=${language}`;
          const response = await fetch(url);
          if (!response.ok) {
            notifySupportApiServerError(response.status);
            throw new Error(`Error ${response.status}: ${response.statusText}`);
          }
          const accessories = await response.json();
          newAccessoriesByModel[vehicle.modelId] = accessories;
          newLoadingStates[vehicle.modelId] = false;
        } catch (error) {
          notifySupportApiNetworkError(error);
          newErrorStates[vehicle.modelId] = error.message;
          newLoadingStates[vehicle.modelId] = false;
        }
      }

      setAccessoriesByModel(newAccessoriesByModel);
      setLoadingStates(newLoadingStates);
      setErrorStates(newErrorStates);
    };

    loadAccessoriesForModels();
  }, [selectedVehicles, language]); // Recargar cuando cambie el idioma

  const handleAccessoryToggle = (accessoryId, vehicleId) => {
    setSelectedAccessories(prev => {
      const newSelection = { ...prev };
      
      if (!newSelection[vehicleId]) {
        newSelection[vehicleId] = [];
      }
      
      const isSelected = newSelection[vehicleId].includes(accessoryId);
      const vehicle = selectedVehicles.find(v => v.id === vehicleId);
      const modelAccessories = vehicle ? (accessoriesByModel[vehicle.modelId] || []) : [];
      const accessory = modelAccessories.find(a => a.id === accessoryId);
      const isInsurance = accessory?.category === 'INSURANCE';
      
      if (isSelected) {
        newSelection[vehicleId] = newSelection[vehicleId].filter(id => id !== accessoryId);
      } else {
        // Al seleccionar un seguro (INSURANCE), solo puede haber uno por bicicleta: quitar otros seguros de esta bicicleta
        if (isInsurance) {
          const insuranceIds = modelAccessories.filter(a => a.category === 'INSURANCE').map(a => a.id);
          newSelection[vehicleId] = newSelection[vehicleId].filter(id => !insuranceIds.includes(id));
        }
        newSelection[vehicleId] = [...newSelection[vehicleId], accessoryId];
      }
      
      // Actualizar el contexto de reserva
      updateBookingData({ selectedAccessories: newSelection });
      
      // Notificar al componente padre sobre el cambio de pricing
      if (onPricingUpdate) {
        onPricingUpdate(newSelection, accessoriesByModel);
      }
      
      return newSelection;
    });
  };

  const calculateAccessoryPrice = (accessory, durationInDays) => {
    if (accessory.accessoryPriceType === 'FIXED') {
      return accessory.price;
    } else {
      // DAILY price
      return accessory.price * durationInDays;
    }
  };

  const getDurationInDays = () => {
    if (!selectedDates?.start || !selectedDates?.end) return 1;
    const start = new Date(selectedDates.start);
    const end = new Date(selectedDates.end);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(1, diffDays);
  };

  const getAccessoryPriceText = (accessory) => {
    const durationInDays = getDurationInDays();
    const price = calculateAccessoryPrice(accessory, durationInDays);
    
    if (accessory.accessoryPriceType === 'FIXED') {
      return formatPriceWithEuro(price);
    } else {
      return `${formatPricePerDay(accessory.price, getTranslation('date.day', language))} (${formatPriceWithEuro(price)} ${getTranslation('accessory.price.total', language)})`;
    }
  };

  // Función para obtener el precio de un vehículo usando la misma lógica que PriceBreakdown
  const getVehiclePrice = (vehicle) => {
    if (!selectedDates?.start || !selectedDates?.end) return null;

    const durationInDays = calculateDurationInDays(selectedDates.start, selectedDates.end);

    // Prioridad 1: Usar totalPrice del backend (precio calculado con temporadas)
    if (vehicle.totalPrice && typeof vehicle.totalPrice === 'number' && vehicle.totalPrice > 0) {
      return vehicle.totalPrice;
    }
    
    // Prioridad 2: Fallback usando pricePerDay del backend
    if (vehicle.pricePerDay && typeof vehicle.pricePerDay === 'number' && vehicle.pricePerDay > 0) {
      return vehicle.pricePerDay * durationInDays;
    }
    
    // Prioridad 3: Fallback usando basePrice del backend
    if (vehicle.basePrice && typeof vehicle.basePrice === 'number' && vehicle.basePrice > 0) {
      return vehicle.basePrice * durationInDays;
    }
    
    // Último recurso: usar precio base del modelo
    if (vehicle.basePriceModel && typeof vehicle.basePriceModel === 'number' && vehicle.basePriceModel > 0) {
      return vehicle.basePriceModel * durationInDays;
    }
    
    // Si no hay precio disponible, retornar null para ocultarlo
    return null;
  };

  const getAccessoryIcon = (category) => {
    const iconMap = {
      'HELMET': Shield,
      'LOCK': Lock,
      'GLOVES': Hand,
      'BACKPACK': Backpack,
      'GPS': MapPin,
      'PEDAL': Zap,
      'INSURANCE': Heart,
      'OTHER': Package
    };
    return iconMap[category] || Package;
  };

  const getAccessoryIconColor = (category) => {
    const colorMap = {
      'HELMET': 'text-blue-500',
      'LOCK': 'text-gray-600',
      'GLOVES': 'text-orange-500',
      'BACKPACK': 'text-green-500',
      'GPS': 'text-purple-500',
      'PEDAL': 'text-yellow-500',
      'INSURANCE': 'text-red-500',
      'OTHER': 'text-primary'
    };
    return colorMap[category] || 'text-primary';
  };


  // Verificar si hay algún estado de carga activo
  const isLoading = Object.values(loadingStates).some(loading => loading);
  
  // Verificar si hay algún error
  // const hasError = Object.values(errorStates).some(error => error !== null);

  // Validar que selectedVehicles sea un array
  if (!Array.isArray(selectedVehicles)) {
    return (
      <div className="accessory-selection-light">
        <div className="max-w-7xl mx-auto md:p-6">
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
          <div className="flex gap-2 items-center text-red-500">
            <AlertCircle size={20} />
            <span>Error: No se han seleccionado bicicletas correctamente</span>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accessory-selection-light">
      <div className="max-w-7xl mx-auto md:p-6">
      
      {/* Spinner de carga general - cuando se están cargando todos los accesorios inicialmente */}
      {isLoading && Object.keys(accessoriesByModel).length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 mb-8">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-EBC122 mb-4"></div>
          <p className="text-2c3e50 text-lg font-medium">{getTranslation('accessory.loading', language) || 'Cargando accesorios...'}</p>
        </div>
      )}

      {/* Resumen de precios - Mostrar arriba en móviles */}
      <div className="lg:hidden mb-6">
        <PriceBreakdown 
          selectedVehicles={selectedVehicles}
          selectedDates={selectedDates}
          deliveryType={bookingData.deliveryOption?.type || 'pickup'}
          selectedAccessories={selectedAccessories}
          accessoriesByModel={accessoriesByModel}
          pricing={bookingData.pricing}
          showInsuranceWarnings
        />
      </div>

      {/* Layout principal */}
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Contenido principal */}
        <div className="flex-1">
          {/* Resumen de bicicletas seleccionadas */}
          <div className="rounded-xl p-6 mb-6">
            <h3 className="font-semibold mb-4 text-2c3e50 flex items-center gap-2">
              <Package size={20} className="text-primary" />
              {getTranslation('accessory.selected.bikes', language)}
            </h3>
            <div className="space-y-3">
              {selectedVehicles.map((vehicle, index) => {
                const vehiclePrice = getVehiclePrice(vehicle);
                return (
                  <div key={index} className="flex justify-between items-center p-4 bg-background border border-gray-custom rounded-lg hover:border-primary transition-colors duration-200">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 bg-primary rounded-full"></div>
                      <span className="font-medium text-2c3e50">{vehicle.model}</span>
                      <span className="text-6c757d">({vehicle.size})</span>
                    </div>
                    {vehiclePrice !== null && (
                      <span className="font-semibold text-primary">{formatPriceWithEuro(vehiclePrice)}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Selección de accesorios por bicicleta */}
          <div className="space-y-8">
            {selectedVehicles.map((vehicle, vehicleIndex) => {
              const modelAccessories = [...(accessoriesByModel[vehicle.modelId] || [])].sort(
                (a, b) => (a.category === 'INSURANCE' ? 0 : 1) - (b.category === 'INSURANCE' ? 0 : 1)
              );
              const isLoadingModel = loadingStates[vehicle.modelId] || false;
              const modelError = errorStates[vehicle.modelId];

              return (
                <div key={vehicleIndex} className="rounded-xl md:p-6">
                  <h3 className="text-lg font-semibold text-2c3e50 mb-6 flex items-center gap-2">
                    <Package size={20} className="text-primary" />
                    {getTranslation('accessory.for.bike', language)}: {vehicle.model} ({vehicle.size})
                  </h3>
                  
                  {isLoadingModel ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-4 border-gray-200 border-t-EBC122 mb-4"></div>
                      <span className="text-2c3e50 text-base font-medium">{getTranslation('accessory.loading', language) || 'Cargando accesorios...'}</span>
                    </div>
                  ) : modelError ? (
                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                      <div className="flex gap-2 items-center text-red-500">
                        <AlertCircle size={20} />
                        <span>{modelError}</span>
                      </div>
                    </div>
                  ) : modelAccessories.length === 0 ? (
                    <div className="text-center py-8 text-6c757d">
                      <Package size={48} className="mx-auto mb-4 text-gray-400" />
                      <p>No hay accesorios disponibles para este modelo de bicicleta.</p>
                    </div>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-4">
                      {modelAccessories.map((accessory) => {
                        const isSelected = selectedAccessories[vehicle.id]?.includes(accessory.id) || false;
                        
                        return (
                          <div 
                            key={accessory.id} 
                            className={`accessory-card bg-background border rounded-xl p-5 transition-all duration-200 cursor-pointer ${
                              isSelected 
                                ? 'border-primary bg-primary/5 shadow-lg shadow-primary/20' 
                                : 'border-gray-custom hover:border-primary/50 hover:bg-gray-custom/10'
                            }`}
                            onClick={() => handleAccessoryToggle(accessory.id, vehicle.id)}
                          >
                            {/* Imagen del accesorio */}
                            <div className="mb-4">
                              <div className="w-full h-40 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg overflow-hidden relative flex items-center justify-center">
                                <img 
                                  src={accessory.imageUrl}
                                  alt={accessory.name}
                                  className="max-w-full max-h-full object-contain object-center"
                                  onError={(e) => {
                                    // Fallback a icono si la imagen falla
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                                <div className="w-full h-full bg-primary/20 rounded-full flex items-center justify-center absolute inset-0" style={{display: 'none'}}>
                                  {(() => {
                                    const IconComponent = getAccessoryIcon(accessory.category);
                                    const iconColor = getAccessoryIconColor(accessory.category);
                                    return <IconComponent size={32} className={iconColor} />;
                                  })()}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-3 flex-1">
                                {/* Checkbox personalizado más grande */}
                                <div className={`custom-checkbox w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-200 ${
                                  isSelected 
                                    ? 'bg-primary border-primary' 
                                    : 'border-gray-custom hover:border-primary'
                                }`}>
                                  {isSelected && (
                                    <CheckCircle size={16} className="text-primary-dark" />
                                  )}
                                </div>
                                <div className="flex-1">
                                  <h4 className="font-semibold text-2c3e50 text-lg">
                                    {accessory.name}
                                  </h4>
                                </div>
                              </div>
                            </div>
                            
                            {accessory.description && (
                              <p className="text-sm text-6c757d mb-4 leading-relaxed">
                                {accessory.description}
                              </p>
                            )}
                            
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-6c757d">
                                  {accessory.accessoryPriceType === 'FIXED' 
                                    ? getTranslation('accessory.price.fixed', language)
                                    : getTranslation('accessory.price.daily', language)
                                  }
                                </span>
                              </div>
                              <div className="text-right">
                                <span className="block font-bold text-lg text-gray-500">
                                  {getAccessoryPriceText(accessory)}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Resumen de precios - Solo en desktop */}
        <div className="hidden lg:block lg:w-80">
          <div className="lg:sticky lg:top-4 space-y-4">
            <PriceBreakdown
              selectedVehicles={selectedVehicles}
              selectedDates={selectedDates}
              deliveryType={bookingData.deliveryOption?.type || 'pickup'}
              selectedAccessories={selectedAccessories}
              accessoriesByModel={accessoriesByModel}
              pricing={bookingData.pricing}
              showInsuranceWarnings
            />
            <button
              className="w-full px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
              onClick={() => onNext({
                selectedAccessories,
                accessoriesByModel
              })}
            >
              {getTranslation('common.continue', language)}
            </button>
          </div>
        </div>
      </div>

      {/* Botones de navegación - en desktop el continuar está bajo el desglose de precio */}
      <div className="flex sm:flex-row justify-between mt-8 gap-4 sm:gap-0">
        <button
            class="px-8 py-4 bg-primary text-2c3e50 border border-EBC122 bg-transparent font-semibold rounded-lg flex items-center gap-2"
          onClick={onBack}
        >
          {/* <Minus size={16} /> */}
          {getTranslation('common.back', language)}
        </button>
        <button
            class="px-8 py-4 bg-primary text-2c3e50 font-semibold rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg lg:hidden"
          onClick={() => onNext({
            selectedAccessories,
            accessoriesByModel
          })}
        >
          {getTranslation('common.continue', language)}
          {/* <Plus size={16} /> */}
        </button>
      </div>
      </div>
    </div>
  );
};

export default AccessorySelection; 